import _ from 'lodash';
import {
  keyControlIncludedEquipmentCameraSelectionVulcan,
  keyControlAdditionalEquipmentVulcan,
  keyMonitorAdditionalEquipmentVulcan,
  keyProtectAdditionalEquipmentVulcan,
  keyProtectIncludedEquipmentCameraSelectionVulcan,
  controlIncludedPoeTurretCameraVulcan,
  protectIncludedPoeTurretCameraVulcan,
  controlIncludedPoeSwitchVulcan,
  smartCameraAdditionalEquipmentVulcan,
  smartCameraIncludedEquipmentCameraSelectionVulcan,
  smartCameraIncludedPoeTurretCameraVulcan,
  temperatureSensorVulcan,
  easyThreeYearTemperatureSensorVulcan,
  easyFiveYearTemperatureSensorVulcan,
  typePoeCamera,
  typeSwitch,
  fivePortPoeSwitchVulcan,
  fivePortPoeSwitchPaidVulcan
} from '../../../../utils/constant';

export const hidePoeSwitchVulcanOptionTSB = (radioSelection, fields) => {
  const updateField = fields;
  if (radioSelection === 'control') {
    updateField[
      keyControlIncludedEquipmentCameraSelectionVulcan
    ].controlIncludedPoeSwitchVulcan.isVisible = false;
    updateField[
      keyControlIncludedEquipmentCameraSelectionVulcan
    ].controlIncludedPoeSwitchVulcan.value = 0;
  }
};

/**
 * Syncs the included POE switch option visibility and value with the current plan selection.
 * Only the section matching radioSelection (protect / control / smartCa) has its included
 * POE switch shown and set to 1; the other sections' included POE switch is hidden and set to 0.
 */
export const updatePoeSwitchVulcanOptionInIncludedSectionTSB = (
  radioSelection,
  fields
) => {
  const sectionConfig = [
    {
      selection: 'control',
      sectionKey: keyControlIncludedEquipmentCameraSelectionVulcan,
      fieldName: controlIncludedPoeSwitchVulcan
    }
  ];

  sectionConfig.forEach(({ selection, sectionKey, fieldName }) => {
    const isActive = radioSelection === selection;
    const section = fields[sectionKey];
    if (section && section[fieldName]) {
      section[fieldName].isVisible = isActive;
      section[fieldName].value = isActive ? 1 : 0;
    }
  });
};

// Returns the total temperature sensor count for the current equipment section (monitor/protect/control)
// across the given payment types (oneTime, 3-year easy, 5-year easy).
export const countTemperatureSensorAll = (formFields, radioSelection, inputKeys) => {
  const getObjectValue = (obj, path) => _.get(obj, `${path}.value`, 0);
  // Payment key (e.g. oneTimePayment) -> temperature sensor field name in that section
  const keyToSensorField = {
    oneTimePayment: temperatureSensorVulcan,
    threeYearEasyPayment: easyThreeYearTemperatureSensorVulcan,
    fiveYearEasyPayment: easyFiveYearTemperatureSensorVulcan
  };
  // Section (monitor/protect/control) -> form key for that section's additional equipment
  const sectionBySelection = {
    monitor: keyMonitorAdditionalEquipmentVulcan,
    protect: keyProtectAdditionalEquipmentVulcan,
    control: keyControlAdditionalEquipmentVulcan
  };
  let keys;
  if (Array.isArray(inputKeys)) {
    keys = inputKeys;
  } else if (typeof inputKeys === 'string') {
    keys = [inputKeys];
  } else {
    keys = [];
  }
  const sectionKey = sectionBySelection[radioSelection];
  if (!sectionKey) return 0;
  // Sum .value for each payment type's temperature sensor field in this section
  return keys.reduce((total, key) => {
    const fieldName = keyToSensorField[key];
    return total + (fieldName ? getObjectValue(formFields, `${sectionKey}.${fieldName}`) : 0);
  }, 0);
};

/**
 * Returns total POE camera count for the given plan and payment type.
 * Sums: (1) included section POE turret camera value, (2) additional equipment POE dome camera
 * for that payment type (one-time, 3-year easy, or 5-year easy).
 */
export const countPoeCameras = (formFields, radioSelection, paymentType) => {
  const fieldMapping = {
    'oneTimePayment': 'poeDomeCamera',
    'threeYearEasyPayment': 'easyThreeYearPoeDomeCameraVulcan',
    'fiveYearEasyPayment': 'easyFiveYearPoeDomeCameraVulcan',
  };
  let poeCameraCount;

  switch (radioSelection) {
    case 'control':
      poeCameraCount =
        Number(_.get(formFields, `${keyControlIncludedEquipmentCameraSelectionVulcan}.${controlIncludedPoeTurretCameraVulcan}.value`, 0)) +
        _.get(formFields, `${keyControlAdditionalEquipmentVulcan}.${fieldMapping[paymentType]}.value`, 0);
      break;
    case 'protect':
      poeCameraCount =
        Number(_.get(formFields, `${keyProtectIncludedEquipmentCameraSelectionVulcan}.${protectIncludedPoeTurretCameraVulcan}.value`, 0)) +
        _.get(formFields, `${keyProtectAdditionalEquipmentVulcan}.${fieldMapping[paymentType]}.value`, 0);
      break;
    case 'smartCa':
      poeCameraCount =
        Number(_.get(formFields, `${smartCameraIncludedEquipmentCameraSelectionVulcan}.${smartCameraIncludedPoeTurretCameraVulcan}.value`, 0)) +
        _.get(formFields, `${smartCameraAdditionalEquipmentVulcan}.${fieldMapping[paymentType]}.value`, 0);
      break;
    default:
      poeCameraCount = 0;
  }

  return poeCameraCount;
};

// Additional-equipment section key for each plan radio selection.
const additionalSectionByRadio = {
  control: keyControlAdditionalEquipmentVulcan,
  protect: keyProtectAdditionalEquipmentVulcan,
  smartCa: smartCameraAdditionalEquipmentVulcan
};

// Plan's included PoE turret camera location for each radio selection.
const includedPoeTurretByRadio = {
  control: {
    sectionKey: keyControlIncludedEquipmentCameraSelectionVulcan,
    field: controlIncludedPoeTurretCameraVulcan
  },
  protect: {
    sectionKey: keyProtectIncludedEquipmentCameraSelectionVulcan,
    field: protectIncludedPoeTurretCameraVulcan
  },
  smartCa: {
    sectionKey: smartCameraIncludedEquipmentCameraSelectionVulcan,
    field: smartCameraIncludedPoeTurretCameraVulcan
  }
};

/**
 * Total POE cameras that drive the consolidated one-time POE switch: every
 * POE-camera field in the given additional-equipment section (any payment
 * tier, matched by product type) plus the plan's included POE turret camera.
 */
export const countAllPoeCamerasInSection = (formFields, sectionName, radioSelection) => {
  const section = (sectionName && formFields[sectionName]) || {};
  const additionalCount = Object.keys(section).reduce((acc, k) => {
    const field = section[k];
    return acc + (field && field.productType === typePoeCamera
      ? parseInt(field.value || 0, 10)
      : 0);
  }, 0);

  const turretConfig = includedPoeTurretByRadio[radioSelection];
  let includedTurret = 0;
  if (turretConfig) {
    includedTurret = Number(
      _.get(formFields, `${turretConfig.sectionKey}.${turretConfig.field}.value`, 0)
    );
  }

  return additionalCount + includedTurret;
};

/**
 * Consolidated one-time POE switch logic. All POE cameras across every payment
 * tier (plus the plan's included POE turret camera) drive a single pair of
 * one-time switches in the plan's additional-equipment section:
 *  - Free 5-port switch: visible for total >= 1 (covers quantities 1–4), value 1.
 *  - Paid 5-port switch: visible for total > 4; value 1 for 5–8, value 2 for 9–12.
 */
export const syncOneTimePoeSwitchFromCameras = (fields, radioSelection) => {
  const sectionKey = additionalSectionByRadio[radioSelection];
  const section = sectionKey && fields[sectionKey];
  if (!section) return;

  const total = countAllPoeCamerasInSection(fields, sectionKey, radioSelection);
  // eslint-disable-next-line no-nested-ternary
  const paidValue = total >= 9 ? 2 : total >= 5 ? 1 : 0;

  Object.keys(section).forEach(k => {
    const field = section[k];
    if (!field || field.productType !== typeSwitch) return;
    if (field.name === fivePortPoeSwitchVulcan) {
      field.isVisible = total > 0;
      field.value = total > 0 ? 1 : 0;
    } else if (field.name === fivePortPoeSwitchPaidVulcan) {
      field.isVisible = total > 4;
      field.value = paidValue;
    }
  });
};
